import date from "date-and-time";
import clsx from "clsx";

import { ITicketInsightWeek } from "../../../models/Tickets/TicketInsight";
import { Card, EmptyState } from "./InsightWidgets";
import {
  categoryBarClassMap,
  categoryLabels,
  categoryNames,
  categoryOrder,
} from "./insightMeta";

interface WeeklyTrendCardProps {
  weeks: ITicketInsightWeek[];
}

const CHART_HEIGHT = 140;

/**
 * Created vs resolved per week. The created bar is stacked by AI category so a
 * bad week is visibly "a bug week" or "an integration week". Tickets that have
 * not been analysed yet show as the grey remainder of the bar.
 */
export default function WeeklyTrendCard({ weeks }: WeeklyTrendCardProps) {
  const max = Math.max(1, ...weeks.map((w) => Math.max(w.created, w.resolved)));
  const totalCreated = weeks.reduce((s, w) => s + w.created, 0);
  const totalResolved = weeks.reduce((s, w) => s + w.resolved, 0);

  const usedCategories = categoryOrder.filter((c) =>
    weeks.some((w) => (w.byCategory[categoryNames[c]] ?? 0) > 0),
  );

  return (
    <Card
      title="Volume by week"
      description={`${totalCreated} created, ${totalResolved} resolved or closed in this range. Created bars are coloured by AI category.`}
    >
      {weeks.length === 0 ? (
        <EmptyState>No tickets in this scope.</EmptyState>
      ) : (
        <div>
          <div className="overflow-x-auto">
            <div
              className="flex min-w-full items-end gap-2 border-b border-gray-200 pb-1"
              style={{ height: CHART_HEIGHT + 8 }}
            >
              {weeks.map((week) => {
                const createdHeight = (week.created / max) * CHART_HEIGHT;
                const resolvedHeight = (week.resolved / max) * CHART_HEIGHT;
                const categorised = Object.values(week.byCategory).reduce(
                  (s, v) => s + v,
                  0,
                );
                const unanalysed = Math.max(0, week.created - categorised);

                return (
                  <div
                    key={week.weekStart}
                    className="flex min-w-[2.5rem] flex-1 flex-col items-center justify-end"
                    title={`Week of ${date.format(new Date(week.weekStart), "DD MMM")}: ${week.created} created, ${week.resolved} resolved`}
                  >
                    <div className="flex w-full items-end justify-center gap-0.5">
                      <div
                        className="flex w-1/2 flex-col-reverse overflow-hidden rounded-t bg-gray-100"
                        style={{ height: Math.max(createdHeight, week.created ? 2 : 0) }}
                      >
                        {categoryOrder.map((category) => {
                          const name = categoryNames[category];
                          const value = week.byCategory[name] ?? 0;
                          if (!value) return null;
                          return (
                            <div
                              key={name}
                              className={categoryBarClassMap[category]}
                              style={{ height: `${(value / week.created) * 100}%` }}
                              title={`${categoryLabels[category]}: ${value}`}
                            />
                          );
                        })}
                        {unanalysed > 0 ? (
                          <div
                            className="bg-gray-300"
                            style={{ height: `${(unanalysed / week.created) * 100}%` }}
                            title={`Not yet analysed: ${unanalysed}`}
                          />
                        ) : null}
                      </div>
                      <div
                        className="w-1/2 rounded-t bg-green-400"
                        style={{ height: Math.max(resolvedHeight, week.resolved ? 2 : 0) }}
                        title={`Resolved: ${week.resolved}`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex min-w-full gap-2 pt-1">
              {weeks.map((week) => (
                <div
                  key={week.weekStart}
                  className="min-w-[2.5rem] flex-1 text-center text-[10px] leading-tight text-gray-500"
                >
                  {date.format(new Date(week.weekStart), "DD MMM")}
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600">
            {usedCategories.map((category) => (
              <span key={category} className="inline-flex items-center gap-1">
                <span className={clsx("h-2.5 w-2.5 rounded-sm", categoryBarClassMap[category])} />
                {categoryLabels[category]}
              </span>
            ))}
            <span className="inline-flex items-center gap-1">
              <span className="h-2.5 w-2.5 rounded-sm bg-gray-300" />
              Not analysed
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="h-2.5 w-2.5 rounded-sm bg-green-400" />
              Resolved / closed
            </span>
          </div>
        </div>
      )}
    </Card>
  );
}
